import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { formatDate } from '../utils/dateFormat';
import axios from 'axios';
import { toast } from 'sonner';

function ApplicationPage() {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [existingApplication, setExistingApplication] = useState(null);
  
  const [formData, setFormData] = useState({
    // 1. Current role and organization
    currentRole: '',
    // 2. LinkedIn Profile URL (optional)
    linkedInProfile: '',
    // 3. Years of experience
    yearsExperience: '',
    // 4. Primary domain of expertise
    primaryDomain: '',
    // 5. Impact without authority example
    impactWithoutAuthority: '',
    // 6. Most commercially meaningful outcome
    commercialOutcome: '',
    // 7. Why PATH is relevant now
    whyPathNow: '',
    // 8. Success vision in 12 months
    successVision: '',
    // 9. How you'll create space for commitment
    commitmentSpace: '',
    // 10. How you heard about PATH
    howHeardAbout: '',
    // 11. Additional information (optional)
    additionalInfo: '',
    // 12. Cohort commitment checkbox
    cohortCommitment: false,
    // Terms agreement
    agreedToTerms: false
  });
  
  const [errors, setErrors] = useState({});
  
  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/login');
      return;
    }
    
    if (user) {
      checkExistingApplication();
    }
  }, [user, authLoading]);
  
  const checkExistingApplication = async () => {
    try {
      const response = await axios.get('/api/applications/my');
      if (response.data.hasApplied) {
        setExistingApplication(response.data.application);
      }
    } catch (error) {
      console.error('Error checking application:', error);
    } finally {
      setLoading(false);
    }
  };
  
  const validateForm = () => {
    const newErrors = {};
    
    // Required text fields
    if (!formData.currentRole.trim()) {
      newErrors.currentRole = 'Current role and organization is required';
    }
    
    // LinkedIn URL validation (optional but must be valid if provided)
    if (formData.linkedInProfile.trim()) {
      const linkedInRegex = /^(https?:\/\/)?(www\.)?linkedin\.com\/.+/i;
      if (!linkedInRegex.test(formData.linkedInProfile.trim())) {
        newErrors.linkedInProfile = 'Please enter a valid LinkedIn URL';
      }
    }
    
    if (!formData.yearsExperience) {
      newErrors.yearsExperience = 'Years of experience is required';
    }
    
    if (!formData.primaryDomain) {
      newErrors.primaryDomain = 'Primary domain of expertise is required';
    }
    
    // Textarea validations (min 50 characters)
    if (!formData.impactWithoutAuthority.trim()) {
      newErrors.impactWithoutAuthority = 'This field is required';
    } else if (formData.impactWithoutAuthority.trim().length < 50) {
      newErrors.impactWithoutAuthority = 'Please provide more detail (at least 50 characters)';
    }
    
    if (!formData.commercialOutcome.trim()) {
      newErrors.commercialOutcome = 'This field is required';
    } else if (formData.commercialOutcome.trim().length < 50) {
      newErrors.commercialOutcome = 'Please provide more detail (at least 50 characters)';
    }
    
    if (!formData.whyPathNow.trim()) {
      newErrors.whyPathNow = 'This field is required';
    } else if (formData.whyPathNow.trim().length < 50) {
      newErrors.whyPathNow = 'Please provide more detail (at least 50 characters)';
    }
    
    if (!formData.successVision.trim()) {
      newErrors.successVision = 'This field is required';
    } else if (formData.successVision.trim().length < 50) {
      newErrors.successVision = 'Please provide more detail (at least 50 characters)';
    }
    
    if (!formData.commitmentSpace.trim()) {
      newErrors.commitmentSpace = 'This field is required';
    } else if (formData.commitmentSpace.trim().length < 50) {
      newErrors.commitmentSpace = 'Please provide more detail (at least 50 characters)';
    }
    
    if (!formData.howHeardAbout) {
      newErrors.howHeardAbout = 'Please tell us how you heard about PATH';
    }
    
    // Required checkboxes
    if (!formData.cohortCommitment) {
      newErrors.cohortCommitment = 'You must confirm your commitment to the cohort';
    }
    
    if (!formData.agreedToTerms) {
      newErrors.agreedToTerms = 'You must agree to the terms';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      toast.error('Please fix the errors in the form');
      return;
    }
    
    setSubmitting(true);
    
    try {
      const response = await axios.post('/api/applications', formData);
      toast.success('Application submitted successfully!');
      setExistingApplication(response.data.application);
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to submit application';
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };
  
  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-amber-100 text-amber-700 border-amber-200',
      under_review: 'bg-blue-100 text-blue-700 border-blue-200',
      accepted: 'bg-emerald-100 text-emerald-700 border-emerald-200',
      rejected: 'bg-red-100 text-red-700 border-red-200',
      waitlisted: 'bg-violet-100 text-violet-700 border-violet-200'
    };
    
    const labels = {
      pending: 'Pending Review',
      under_review: 'Under Review',
      accepted: 'Accepted',
      rejected: 'Not Selected',
      waitlisted: 'Waitlisted'
    };
    
    return (
      <span className={`px-4 py-2 rounded-full text-sm font-medium border ${styles[status] || styles.pending}`}>
        {labels[status] || status}
      </span>
    );
  };
  
  if (authLoading || loading) {
    return (
      <div className="max-w-3xl mx-auto">
        <Skeleton className="h-8 w-64 mb-4" />
        <Skeleton className="h-96 w-full" />
      </div>
    );
  }
  
  // Show existing application status
  if (existingApplication) {
    return (
      <div className="max-w-3xl" data-testid="application-status">
        {/* Page Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-semibold">The PATH Programme Application</h1>
          <p className="text-sm text-muted-foreground mt-1">Your application status</p>
        </div>
        
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="space-y-8"
        >
          <div className="bg-card border border-border rounded-[var(--radius)] p-8 elev-1 text-center">
            <div className="mb-6">
              {getStatusBadge(existingApplication.status)}
            </div>
            
            <h2 className="h2 text-2xl mb-4">Application Submitted</h2>
            
            <p className="body text-muted-foreground mb-6 max-w-lg mx-auto">
              {existingApplication.status === 'pending' && 
                "Your application is in our queue. We will review it within 5-7 business days and notify you of our decision."}
              {existingApplication.status === 'under_review' && 
                "Great news! Your application is currently being reviewed by our team. You will hear from us soon."}
              {existingApplication.status === 'accepted' && 
                "Congratulations! You have been accepted into The PATH Programme. Check your email for next steps."}
              {existingApplication.status === 'rejected' && 
                "Thank you for your interest. Unfortunately, we are unable to offer you a spot in this cohort. Please consider applying for future cohorts."}
              {existingApplication.status === 'waitlisted' && 
                "You have been added to our waitlist. We will contact you if a spot becomes available."}
            </p>
            
            <div className="bg-muted/30 rounded-[var(--radius)] p-4 text-sm text-left max-w-md mx-auto">
              <p className="text-muted-foreground mb-2">
                <strong>Submitted:</strong> {formatDate(existingApplication.submittedAt)}
              </p>
              <p className="text-muted-foreground">
                <strong>Role:</strong> {existingApplication.currentRole}
              </p>
            </div>
            
            <div className="mt-8">
              <Button onClick={() => navigate('/dashboard')} variant="outline">
                Back to Dashboard
              </Button>
            </div>
          </div>
        </motion.div>
      </div>
    );
  }
  
  // Show application form
  return (
    <div className="max-w-3xl" data-testid="application-form">
      {/* Page Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-semibold">Apply for The PATH Programme</h1>
        <p className="text-sm text-muted-foreground mt-1">Join a cohort of exceptional leaders</p>
      </div>
      
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="space-y-8"
      >
        {/* Introduction */}
        <div className="bg-gradient-to-br from-violet-50 to-purple-50 dark:from-violet-950/30 dark:to-purple-950/30 border border-violet-200 dark:border-violet-800 rounded-[var(--radius)] p-8">
          <h2 className="h3 text-xl mb-4 text-violet-900 dark:text-violet-100">The PATH Programme</h2>
          <p className="body text-violet-800 dark:text-violet-200 mb-4">
            The PATH Programme is a facilitated development experience designed for senior leaders 
            ready to break free from employee mindset patterns and step into entrepreneurial leadership.
          </p>
          <p className="body text-violet-700 dark:text-violet-300 text-sm">
            Please take your time with this application. We&apos;re looking for calibre over quantity—leaders who 
            are ready to commit fully to their transformation.
          </p>
          <div className="grid sm:grid-cols-3 gap-4 text-sm mt-6">
            <div className="flex items-center gap-2 text-violet-700 dark:text-violet-300">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
              <span>Small cohort</span>
            </div>
            <div className="flex items-center gap-2 text-violet-700 dark:text-violet-300">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span>12-week programme</span>
            </div>
            <div className="flex items-center gap-2 text-violet-700 dark:text-violet-300">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
              </svg>
              <span>Direct facilitation</span>
            </div>
          </div>
        </div>
        
        {/* Application Form */}
        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Section 1: Professional Background */}
          <div className="bg-card border border-border rounded-[var(--radius)] p-8 elev-1 space-y-6">
            <h3 className="font-semibold text-lg border-b border-border pb-4">Professional Background</h3>
            
            {/* 1. Current Role and Organization */}
            <div>
              <label className="block text-sm font-medium mb-2">
                Current role and organisation <span className="text-red-500">*</span>
              </label>
              <Input
                value={formData.currentRole}
                onChange={(e) => setFormData({...formData, currentRole: e.target.value})}
                placeholder="e.g., VP of Engineering at Acme Corp"
                className={errors.currentRole ? 'border-red-500' : ''}
                data-testid="current-role-input"
              />
              {errors.currentRole && (
                <p className="text-red-500 text-sm mt-1">{errors.currentRole}</p>
              )}
            </div>
            
            {/* 2. LinkedIn Profile URL (optional) */}
            <div>
              <label className="block text-sm font-medium mb-2">
                LinkedIn Profile URL <span className="text-muted-foreground text-xs">(optional)</span>
              </label>
              <Input
                value={formData.linkedInProfile}
                onChange={(e) => setFormData({...formData, linkedInProfile: e.target.value})}
                placeholder="https://linkedin.com/in/yourprofile"
                className={errors.linkedInProfile ? 'border-red-500' : ''}
                data-testid="linkedin-profile-input"
              />
              {errors.linkedInProfile && (
                <p className="text-red-500 text-sm mt-1">{errors.linkedInProfile}</p>
              )}
            </div>
            
            {/* 3. Years of Experience */}
            <div>
              <label className="block text-sm font-medium mb-2">
                Years of professional experience <span className="text-red-500">*</span>
              </label>
              <Select 
                value={formData.yearsExperience}
                onValueChange={(value) => setFormData({...formData, yearsExperience: value})}
              >
                <SelectTrigger className={errors.yearsExperience ? 'border-red-500' : ''} data-testid="years-experience-select">
                  <SelectValue placeholder="Select years of experience" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="5-10">5-10 years</SelectItem>
                  <SelectItem value="10-15">10-15 years</SelectItem>
                  <SelectItem value="15-20">15-20 years</SelectItem>
                  <SelectItem value="20+">20+ years</SelectItem>
                </SelectContent>
              </Select>
              {errors.yearsExperience && (
                <p className="text-red-500 text-sm mt-1">{errors.yearsExperience}</p>
              )}
            </div>
            
            {/* 4. Primary Domain of Expertise */}
            <div>
              <label className="block text-sm font-medium mb-2">
                Primary domain of expertise <span className="text-red-500">*</span>
              </label>
              <Select 
                value={formData.primaryDomain}
                onValueChange={(value) => setFormData({...formData, primaryDomain: value})}
              >
                <SelectTrigger className={errors.primaryDomain ? 'border-red-500' : ''} data-testid="primary-domain-select">
                  <SelectValue placeholder="Select your primary domain" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="engineering">Engineering / Technology</SelectItem>
                  <SelectItem value="product">Product Management</SelectItem>
                  <SelectItem value="design">Design / UX</SelectItem>
                  <SelectItem value="operations">Operations / COO</SelectItem>
                  <SelectItem value="finance">Finance / CFO</SelectItem>
                  <SelectItem value="sales">Sales / Revenue</SelectItem>
                  <SelectItem value="marketing">Marketing / Growth</SelectItem>
                  <SelectItem value="hr">People / HR</SelectItem>
                  <SelectItem value="strategy">Strategy / Consulting</SelectItem>
                  <SelectItem value="founder">Founder / CEO</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
              {errors.primaryDomain && (
                <p className="text-red-500 text-sm mt-1">{errors.primaryDomain}</p>
              )}
            </div>
          </div>
          
          {/* Section 2: Your Experience */}
          <div className="bg-card border border-border rounded-[var(--radius)] p-8 elev-1 space-y-6">
            <h3 className="font-semibold text-lg border-b border-border pb-4">Your Experience</h3>
            
            {/* 5. Impact Without Authority */}
            <div>
              <label className="block text-sm font-medium mb-2">
                Describe a time you created impact without formal authority <span className="text-red-500">*</span>
              </label>
              <p className="text-sm text-muted-foreground mb-3">
                Tell us about a situation where you influenced outcomes beyond your role or position.
              </p>
              <Textarea
                value={formData.impactWithoutAuthority}
                onChange={(e) => setFormData({...formData, impactWithoutAuthority: e.target.value})}
                placeholder="Share a specific example of how you drove change or created impact beyond your formal responsibilities..."
                rows={5}
                className={errors.impactWithoutAuthority ? 'border-red-500' : ''}
                data-testid="impact-authority-textarea"
              />
              <div className="flex justify-between mt-1">
                {errors.impactWithoutAuthority ? (
                  <p className="text-red-500 text-sm">{errors.impactWithoutAuthority}</p>
                ) : (
                  <span></span>
                )}
                <span className="text-xs text-muted-foreground">
                  {formData.impactWithoutAuthority.length} characters
                </span>
              </div>
            </div>
            
            {/* 6. Most Commercially Meaningful Outcome */}
            <div>
              <label className="block text-sm font-medium mb-2">
                What is your most commercially meaningful outcome? <span className="text-red-500">*</span>
              </label>
              <p className="text-sm text-muted-foreground mb-3">
                Describe a result you drove that had significant business impact—revenue, cost savings, market share, etc.
              </p>
              <Textarea
                value={formData.commercialOutcome}
                onChange={(e) => setFormData({...formData, commercialOutcome: e.target.value})}
                placeholder="Describe the outcome, your role in achieving it, and the measurable impact..."
                rows={5}
                className={errors.commercialOutcome ? 'border-red-500' : ''}
                data-testid="commercial-outcome-textarea"
              />
              <div className="flex justify-between mt-1">
                {errors.commercialOutcome ? (
                  <p className="text-red-500 text-sm">{errors.commercialOutcome}</p>
                ) : (
                  <span></span>
                )}
                <span className="text-xs text-muted-foreground">
                  {formData.commercialOutcome.length} characters
                </span>
              </div>
            </div>
          </div>
          
          {/* Section 3: Your Vision */}
          <div className="bg-card border border-border rounded-[var(--radius)] p-8 elev-1 space-y-6">
            <h3 className="font-semibold text-lg border-b border-border pb-4">Your Vision</h3>
            
            {/* 7. Why PATH is Relevant Now */}
            <div>
              <label className="block text-sm font-medium mb-2">
                Why is The PATH Programme relevant for you right now? <span className="text-red-500">*</span>
              </label>
              <p className="text-sm text-muted-foreground mb-3">
                What makes this the right moment in your career for this kind of transformation?
              </p>
              <Textarea
                value={formData.whyPathNow}
                onChange={(e) => setFormData({...formData, whyPathNow: e.target.value})}
                placeholder="Explain what's driving your interest in PATH at this point in your journey..."
                rows={5}
                className={errors.whyPathNow ? 'border-red-500' : ''}
                data-testid="why-path-now-textarea"
              />
              <div className="flex justify-between mt-1">
                {errors.whyPathNow ? (
                  <p className="text-red-500 text-sm">{errors.whyPathNow}</p>
                ) : (
                  <span></span>
                )}
                <span className="text-xs text-muted-foreground">
                  {formData.whyPathNow.length} characters
                </span>
              </div>
            </div>
            
            {/* 8. Success Vision in 12 Months */}
            <div>
              <label className="block text-sm font-medium mb-2">
                What does success look like for you in 12 months? <span className="text-red-500">*</span>
              </label>
              <p className="text-sm text-muted-foreground mb-3">
                Paint a picture of where you want to be—professionally, personally, or both.
              </p>
              <Textarea
                value={formData.successVision}
                onChange={(e) => setFormData({...formData, successVision: e.target.value})}
                placeholder="Describe your vision for the next 12 months and what transformation you're seeking..."
                rows={5}
                className={errors.successVision ? 'border-red-500' : ''}
                data-testid="success-vision-textarea"
              />
              <div className="flex justify-between mt-1">
                {errors.successVision ? (
                  <p className="text-red-500 text-sm">{errors.successVision}</p>
                ) : (
                  <span></span>
                )}
                <span className="text-xs text-muted-foreground">
                  {formData.successVision.length} characters
                </span>
              </div>
            </div>
            
            {/* 9. How You'll Create Space for Commitment */}
            <div>
              <label className="block text-sm font-medium mb-2">
                How will you create space for this commitment? <span className="text-red-500">*</span>
              </label>
              <p className="text-sm text-muted-foreground mb-3">
                The PATH Programme requires real investment of time and energy. How will you make room for it?
              </p>
              <Textarea
                value={formData.commitmentSpace}
                onChange={(e) => setFormData({...formData, commitmentSpace: e.target.value})}
                placeholder="Describe how you'll prioritise this programme alongside your other responsibilities..."
                rows={5}
                className={errors.commitmentSpace ? 'border-red-500' : ''}
                data-testid="commitment-space-textarea"
              />
              <div className="flex justify-between mt-1">
                {errors.commitmentSpace ? (
                  <p className="text-red-500 text-sm">{errors.commitmentSpace}</p>
                ) : (
                  <span></span>
                )}
                <span className="text-xs text-muted-foreground">
                  {formData.commitmentSpace.length} characters
                </span>
              </div>
            </div>
          </div>
          
          {/* Section 4: Final Details */}
          <div className="bg-card border border-border rounded-[var(--radius)] p-8 elev-1 space-y-6">
            <h3 className="font-semibold text-lg border-b border-border pb-4">Final Details</h3>
            
            {/* 10. How You Heard About PATH */}
            <div>
              <label className="block text-sm font-medium mb-2">
                How did you hear about The PATH Programme? <span className="text-red-500">*</span>
              </label>
              <Select 
                value={formData.howHeardAbout}
                onValueChange={(value) => setFormData({...formData, howHeardAbout: value})}
              >
                <SelectTrigger className={errors.howHeardAbout ? 'border-red-500' : ''} data-testid="how-heard-select">
                  <SelectValue placeholder="Select how you found us" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="linkedin">LinkedIn</SelectItem>
                  <SelectItem value="twitter">Twitter/X</SelectItem>
                  <SelectItem value="referral">Personal Referral</SelectItem>
                  <SelectItem value="podcast">Podcast</SelectItem>
                  <SelectItem value="article">Article / Blog</SelectItem>
                  <SelectItem value="search">Search Engine</SelectItem>
                  <SelectItem value="event">Event / Conference</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
              {errors.howHeardAbout && (
                <p className="text-red-500 text-sm mt-1">{errors.howHeardAbout}</p>
              )}
            </div>
            
            {/* 11. Additional Information (optional) */}
            <div>
              <label className="block text-sm font-medium mb-2">
                Additional information <span className="text-muted-foreground text-xs">(optional)</span>
              </label>
              <p className="text-sm text-muted-foreground mb-3">
                Anything else you&apos;d like us to know about you or your application?
              </p>
              <Textarea
                value={formData.additionalInfo}
                onChange={(e) => setFormData({...formData, additionalInfo: e.target.value})}
                placeholder="Share any additional context that might be helpful..."
                rows={4}
                data-testid="additional-info-textarea"
              />
            </div>
          </div>
          
          {/* Section 5: Commitments */}
          <div className="bg-card border border-border rounded-[var(--radius)] p-8 elev-1 space-y-6">
            <h3 className="font-semibold text-lg border-b border-border pb-4">Commitments</h3>
            
            {/* 12. Cohort Commitment Checkbox */}
            <div className="flex items-start space-x-3">
              <Checkbox
                id="cohortCommitment"
                checked={formData.cohortCommitment}
                onCheckedChange={(checked) => setFormData({...formData, cohortCommitment: checked})}
                className={errors.cohortCommitment ? 'border-red-500' : ''}
                data-testid="cohort-commitment-checkbox"
              />
              <div>
                <label htmlFor="cohortCommitment" className="text-sm font-medium cursor-pointer">
                  I commit to fully participating in the cohort programme <span className="text-red-500">*</span>
                </label>
                <p className="text-sm text-muted-foreground mt-1">
                  I understand that The PATH Programme requires active participation, attendance at scheduled sessions, 
                  completion of assignments, and engagement with my cohort peers. I am ready to invest the time and 
                  energy needed to get the most from this experience.
                </p>
                {errors.cohortCommitment && (
                  <p className="text-red-500 text-sm mt-1">{errors.cohortCommitment}</p>
                )}
              </div>
            </div>
            
            {/* Terms Checkbox */}
            <div className="flex items-start space-x-3">
              <Checkbox
                id="terms"
                checked={formData.agreedToTerms}
                onCheckedChange={(checked) => setFormData({...formData, agreedToTerms: checked})}
                className={errors.agreedToTerms ? 'border-red-500' : ''}
                data-testid="terms-checkbox"
              />
              <div>
                <label htmlFor="terms" className="text-sm font-medium cursor-pointer">
                  I agree to the terms and conditions <span className="text-red-500">*</span>
                </label>
                <p className="text-sm text-muted-foreground mt-1">
                  By checking this box, you confirm that you have read and agree to our{' '}
                  <a href="/terms" className="text-primary hover:underline">terms of service</a> and{' '}
                  <a href="/privacy" className="text-primary hover:underline">privacy policy</a>.
                </p>
                {errors.agreedToTerms && (
                  <p className="text-red-500 text-sm mt-1">{errors.agreedToTerms}</p>
                )}
              </div>
            </div>
          </div>
          
          {/* Submit */}
          <div className="flex justify-end gap-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate('/dashboard')}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={submitting}
              className="bg-primary hover:bg-primary/90"
              data-testid="submit-application-btn"
            >
              {submitting ? (
                <>
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Submitting...
                </>
              ) : (
                'Submit Application'
              )}
            </Button>
          </div>
        </form>
      </motion.div>
    </div>
  );
}

export default ApplicationPage;
